import React, { useEffect, useState, useRef } from 'react';
import Layout from '../../components/layout/Layout';
import { getData, postData, putData, deleteData } from '../../services/apiService';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    IconButton, Button, Modal, TextField, Select, MenuItem, Pagination, InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import Progress from '../../hooks/useProgressBar';
import useDebounce from '../../hooks/useDebounce';

const AddRole = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const isFetched = useRef(false);
    const [showRoleModal, addRoleShowModal] = useState(false);
    const [updateRoleModalShow, updateRoleModal] = useState(false);
    const [formData, setFormData] = useState({ role_name: "" });
    const [selectedRole, setSelectedRole] = useState(null);

    // Pagination and search states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (!isFetched.current) {
            fetchData();
        }
    }, [page, rowsPerPage, debouncedSearchQuery]);

    // Fetch all Role from the API with pagination and search
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getData(`/get-all-role?page=${page}&limit=${rowsPerPage}&search=${debouncedSearchQuery}`);
            if (response) {
                setData(response.data);
                setTotalPages(response.totalPages);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch data', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1); // Reset to first page on new search
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };

    // Add new Role
    const addRole = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postData('/create-role', formData);
            setFormData({ role_name: "" });
            addRoleShowModal(false);
            fetchData();
            setLoading(false);
        } catch (error) {
            console.error('Failed to post data', error);
            setLoading(false);
        }
    };

    // Edit Role
    const editRole = (id) => {
        const roleData = data.find(dept => dept._id === id);
        setSelectedRole(roleData);
        updateRoleModal(true);
    };

    // Update role
    const updateRole = async () => {
        if (!selectedRole) return;
        const updatedData = {
            role_name: selectedRole.role_name
        };

        try {
            await putData(`/update-role/${selectedRole._id}`, updatedData);
            fetchData();
            updateRoleModal(false);
        } catch (error) {
            console.error('Failed to update role', error);
        }
    };

    // Delete Role
    const deleteRole = async (id) => {
        try {
            await deleteData(`/delete-role/${id}`);
            fetchData();
        } catch (error) {
            console.error("Error:", error);
        }
    };

    // For Add Role Modal
    const handleAddRoleModalClose = (event, reason) => {
        if (reason && reason === "backdropClick") return;
        addRoleShowModal(false);
    };

    // For Update Role Modal
    const handleUpdateRoleModalClose = (event, reason) => {
        if (reason && reason === "backdropClick") return;
        updateRoleModal(false);
    };

    return (
        <Layout>
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>
                        Add Role
                    </Typography>
                    {/* Header: Add Button (left) and Search (right) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, }} >
                        <Button variant="contained" sx={{
                            borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                            '&:hover': {
                                backgroundColor: '#3A3897',
                            },
                        }} onClick={() => addRoleShowModal(true)} >
                            + Add Role
                        </Button>
                        <TextField placeholder="Search Role" value={searchQuery} onChange={handleSearchChange} variant="outlined" size="medium"
                            sx={{
                                width: 350, bgcolor: '#e9ecef', borderRadius: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#757575' }} />
                                    </InputAdornment>
                                ),
                                sx: { bgcolor: '#e9ecef', borderRadius: 2 },
                            }}
                        />
                    </Box>

                    {/* Table */}
                    {/* <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} > */}

                    <TableContainer component={Paper}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopLeftRadius: 8 }}>
                                        ID
                                    </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>
                                        Role Name
                                    </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopRightRadius: 8 }}>
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow key={item._id}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.role_name}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton color="primary" onClick={() => editRole(item._id)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => deleteRole(item._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    '&.Mui-selected': {
                                        bgcolor: '#4B49AC',
                                        color: 'white',
                                    },
                                },
                            }}
                        />
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                            sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                            IconComponent={KeyboardArrowDownIcon} >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                        </Select>
                    </Box>

                    {/* Add Role Modal */}
                    <Modal open={showRoleModal} onClose={handleAddRoleModalClose} aria-labelledby="add-role-modal" >
                        <Box sx={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400,
                            bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 1,
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" component="h2">
                                    Add Role
                                </Typography>
                                <IconButton onClick={() => addRoleShowModal(false)}
                                    sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }} >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <form onSubmit={addRole}>
                                <TextField fullWidth label="Role Name" name="role_name" value={formData.role_name} onChange={handleInputChange} margin="normal" />
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    {/* <Button onClick={() => addRoleShowModal(false)}>Cancel</Button> */}
                                    <Button type="submit" variant="contained" color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>
                                        Save
                                    </Button>
                                </Box>
                            </form>
                        </Box>
                    </Modal>

                    {/* Update Role Modal */}
                    <Modal open={updateRoleModalShow} onClose={handleUpdateRoleModalClose} aria-labelledby="update-role-modal">
                        <Box sx={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper',
                            boxShadow: 24, p: 4, borderRadius: 1,
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" component="h2">
                                    Update Role
                                </Typography>
                                <IconButton
                                    onClick={() => updateRoleModal(false)}
                                    sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }} >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <form>
                                <TextField fullWidth label="Role Name" value={selectedRole?.role_name || ""}
                                    onChange={(e) => setSelectedRole({ ...selectedRole, role_name: e.target.value })} margin="normal" />
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    {/* <Button onClick={() => updateRoleModal(false)}>Cancel</Button> */}
                                    <Button onClick={updateRole} variant="contained" color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>
                                        Save Changes
                                    </Button>
                                </Box>
                            </form>
                        </Box>
                    </Modal>
                </Box>
            }
        </Layout>
    );
};

export default AddRole;